import React, { FC, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  ListItemSecondaryAction,
  Stack,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Store, UserPagePermission } from '../../../../types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ROLES } from '../../../../constants/constants';

type RoleType = keyof typeof ROLES;

type Props = {
  onClose: () => void;
  onSubmit: (payload: {
    username?: string;
    firstName?: string;
    lastName?: string;
    authorities?: RoleType[];
    permissions?: UserPagePermission[];
    imageId?: number | null;
  }) => void;
};

export const UserPagePermissionModalInner: FC<Props> = ({ onClose, onSubmit }) => {
  const { chosenUser } = useSelector((state: Store) => state.users);
  const initialPermissions = chosenUser?.permissions?.map((p) => ({ ...p })) ?? [];
  const [permissions, setPermissions] = useState(initialPermissions);
  const { t } = useTranslation();
  const handleCheckboxChange = (index: number, field: 'canRead' | 'canEdit') => {
    const updated = [...permissions];

    if (field === 'canEdit') {
      const newCanEdit = !updated[index].canEdit;
      updated[index].canEdit = newCanEdit;
      if (newCanEdit) {
        updated[index].canRead = true;
      }
    } else {
      updated[index].canRead = !updated[index].canRead;
    }

    setPermissions(updated);
  };

  const handleSubmit = () => {
    onSubmit({
      permissions,
      username: String(chosenUser?.username),
      firstName: String(chosenUser?.firstName),
      lastName: String(chosenUser?.firstName),
      authorities: chosenUser?.authorities,
      imageId: chosenUser?.imageId,
    });
  };

  const handleCancel = () => {
    setPermissions(initialPermissions);
    onClose();
  };

  const getPageTitle = (page: string, t: TFunction): string => {
    const map: Record<string, string> = {
      PAGE_NEWS: 'dashboard.news',
      PAGE_COURSES: 'dashboard.courses',
      PAGE_PATRICIA_INTRODUCTION: 'dashboard.patriciaIntroduction',
      PAGE_HIGH_ENERGY_SECRETS: 'dashboard.highEnergySecrets',
      PAGE_YOUTUBE: 'dashboard.youtube',
      PAGE_NOTIFICATIONS: 'dashboard.notifications',
      PAGE_LIFESTYLE: 'dashboard.lifestyle',
      PAGE_USER_MANAGEMENT: 'dashboard.users',
    };

    return t(map[page] || page);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ pl: 0, fontSize: '18px' }}>
        Permissions for {chosenUser?.firstName} {chosenUser?.lastName}
      </Typography>
      <List>
        {permissions.map((perm, index) => (
          <ListItem key={perm.page} divider>
            <ListItemText primary={getPageTitle(perm.page, t)} />
            <ListItemSecondaryAction>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={perm.canRead}
                    onChange={() => handleCheckboxChange(index, 'canRead')}
                  />
                }
                label="Read"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={perm.canEdit}
                    onChange={() => handleCheckboxChange(index, 'canEdit')}
                  />
                }
                label="Edit"
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <Box mt={2} textAlign="right">
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Save
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
